import React, { useEffect, useRef } from 'react'
import { Box, Typography, MenuItem, Grid, Button } from '@mui/material'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import { CustomSelect, StandardChip } from '../../../v2/MUI_custom_component'
import ProductCard from './component/ProductCard'
import CartIem from './component/CartIem'
import { getSkuCategoryDetailAction, GetCategoryProductsAction } from './API/Action'
import {
  addProductToCart,
  updateProductQty,
  removeProduct,
  initDP,
  resetDP,
  setDPDetails,
  selectShipToParty
} from './reducer/DpActions'
import {
  selectCurrentCart,
  selectOrderBookingId,
  selectSelectedShipToPartyId,
  selectShipToPartyCarts
} from './reducer/DpSelectors'

import IllustrationWrapper from './../../../v3/Illustrration/IllustrationWrapper'
import NoDataFoundSvg from './../../../v3/Illustrration/components/NoDataFoundSvg';

const CreateDp = () => {
  const [productList, setProductList] = React.useState([])
  const [selectedCategory, setSelectedCategory] = React.useState(null)
  const [selectedFormType, setSelectedFormType] = React.useState(null) 
  const [skuCategories, setSkuCategories] = React.useState([])
  
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const previousManufacturerRef = useRef(null)

  const orderBookingId = useSelector(selectOrderBookingId)
  const selectedShipToPartyId = useSelector(selectSelectedShipToPartyId)
  const cartItems = useSelector(selectCurrentCart)
  const shipToPartyCarts = useSelector(selectShipToPartyCarts)

  const source = location.state?.from;

  const effectiveShipToPartyId =
  location.state?.shipToPartyId ?? selectedShipToPartyId;
  
  useEffect(() => {
    if (
      !effectiveShipToPartyId &&
      Object.keys(shipToPartyCarts).length > 0
      
    ) {
      console.warn(
        'Blocked general cart usage because Ship-To-Party already exists'
      );
    }
  }, [effectiveShipToPartyId, shipToPartyCarts])

  const { selectedSupplier: selectedManufacturer, selectedEntity: selectedCompanyEntity } = useSelector(
    (state) => state.entityReducer
  )
  const DEFAULT_SKU_PAYLOAD = {
    type_id: 0,
    grade_id: 0,
    nb_id: 0,
    od_id: 0,
    area_id: 0,
    sub_grade_id: 0,
    thickness_id: 0,
    coating_id: 0,
    length_id: 0,
  }
  const getDefaultValueFromCategory = (category) => {
    return category.sku_value_details.find(detail => detail.is_default) || 
      (category.sku_value_details.length > 0 ? category.sku_value_details[0] : null)
  }

  const getSkuProductList = async () => {
    const { data, error } = await getSkuCategoryDetailAction(selectedManufacturer, DEFAULT_SKU_PAYLOAD)
    
    if (error || !data?.details) return

    setSkuCategories(data.details)

    const defaultSkuValues = []
    const categories = data.details

    categories.forEach(category => {
      const defaultValue = getDefaultValueFromCategory(category)
      if (defaultValue) {
        defaultSkuValues.push(defaultValue.sku_value_id)
        
        if (category.sku_key === 'TYPE' || category.sku_key === 'GRADE') {
          setSelectedCategory(defaultValue.sku_value_id.toString())
        } else if (category.sku_key === 'FORM_TYPE') {
          setSelectedFormType(defaultValue.sku_value_id.toString())
        }
      }
    })

    if (defaultSkuValues.length > 0) {
      await fetchProducts({ sku_value_id: defaultSkuValues })
    }
  }

  const fetchProducts = async (payload) => {
    const response = await GetCategoryProductsAction(selectedCompanyEntity, payload)
    if (!response.error && response.data) {
      setProductList(response.data)
    }
  }

  useEffect(() => {
    if (!selectedCompanyEntity || !selectedManufacturer) return

    // Initialize DP with order_booking_id if provided from navigation state or PO list
    const passedOrderBookingId = location.state?.order_booking_id || location.state?.orderBookingId
    if (passedOrderBookingId && !orderBookingId) {
      dispatch(initDP(passedOrderBookingId))
    }

    if (previousManufacturerRef.current && 
        previousManufacturerRef.current !== selectedManufacturer) {
      dispatch(resetDP())
    }
    
    previousManufacturerRef.current = selectedManufacturer
    getSkuProductList()
  }, [selectedCompanyEntity, selectedManufacturer, location.state])
  const fetchProductsForSelectedCategories = async (gradeValue = null, formTypeValue = null) => {
    const currentGrade = gradeValue || selectedCategory
    const currentFormType = formTypeValue || selectedFormType
    
    const selectedSkuValues = [currentGrade, currentFormType]
      .filter(Boolean)
      .map(value => parseInt(value))

    if (selectedSkuValues.length === 0) return

    const payload = { sku_value_id: selectedSkuValues }
    const { data, error, message } = await GetCategoryProductsAction(selectedCompanyEntity, payload)
    
    if (!error && data) {
      setProductList(data)
    } else {
      setProductList([])
      if (message) toast.error(message)
    }
  }

  const handleGradeChange = async (event) => {
    const newGradeValue = event.target.value
    setSelectedCategory(newGradeValue)
    await fetchProductsForSelectedCategories(newGradeValue, selectedFormType)
  }

  const getGradeCategory = () => skuCategories?.find(cat => ['GRADE', 'TYPE'].includes(cat.sku_key))
  const getFormTypeCategory = () => skuCategories?.find(cat => cat.sku_key === 'FORM_TYPE')

  const getProductQuantity = (productId) => {
    const cartItem = cartItems.find(item => item.product_id === productId)
    if (!cartItem) return 0
    
    return typeof cartItem.quantity === 'object' 
      ? cartItem.quantity.quantity_in_metric_ton 
      : Number(cartItem.quantity || 0)
  }

  const handleFormTypeClick = (formTypeId) => {
    const newFormTypeValue = formTypeId.toString()
    setSelectedFormType(newFormTypeValue)
    fetchProductsForSelectedCategories(selectedCategory, newFormTypeValue)
  }

  const handleQuantityChange = (product, newQty) => {
    if (newQty <= 0) {
      dispatch(removeProduct(product.product_id, effectiveShipToPartyId))
      return
    }

    const cartItem = cartItems.find(item => item.product_id === product.product_id)
    
    if (cartItem) {
      // Update existing product quantity
      dispatch(updateProductQty(product.product_id, newQty, effectiveShipToPartyId))
    } else {
      const cartProduct = {
        product_id: product.product_id,
        product_name: product.product_name,
        product_image: product.product_image,
        current_price_per_unit: product.current_price_per_unit,
        quantity: {
          quantity_in_bags: newQty * product.meta_data.multiplier,
          quantity_in_metric_ton: newQty,
          multiplier: product.meta_data.multiplier,
          unit: product.meta_data.unit,
          base_unit: product.meta_data.base_unit,
        },
        description: product.description,
        isExistingItem: false,
      }
      
      dispatch(addProductToCart(cartProduct, effectiveShipToPartyId))
    }
  }
  useEffect(() => {
    if (!selectedCategory && !selectedFormType) return;
    dispatch(setDPDetails({
      selectedCategory,
      selectedFormType
    }));
  }, [selectedCategory, selectedFormType]);
  

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h2">
          Select Product
        </Typography>
        <CartIem selectedCompanyData={selectedCompanyEntity} source={source} />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          {getGradeCategory() && (
            <CustomSelect
              label="Sub Category"
              value={selectedCategory}
              onChange={handleGradeChange}
              fullWidth
            >
              {getGradeCategory().sku_value_details.map((grade) => (
                <MenuItem key={grade.sku_value_id} value={grade.sku_value_id.toString()}>
                  <Typography>{grade.sku_value}</Typography>
                </MenuItem>
              ))}
            </CustomSelect>
          )}
        </Grid>
        
        <Grid item xs={6}>
          {getFormTypeCategory() && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="labelD">
                Select Form Type
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {getFormTypeCategory().sku_value_details.map((formType) => {
                  const isSelected = formType.sku_value_id.toString() === selectedFormType
                  return (
                    <StandardChip
                      key={formType.sku_value_id}
                      label={formType.sku_value}
                      selected={isSelected}
                      onClick={() => handleFormTypeClick(formType.sku_value_id)}
                      size="medium"
                      height="unset"
                      sx={{ padding: '4px 8px' }}
                    />
                  )
                })}
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
      
      <Box>
        {productList?.length > 0 ? (
          <Grid container spacing={2}>
            {productList.map((product) => (
              <Grid item xs={12} sm={6} key={product.product_id}>
                <ProductCard
                  imageUrl={product.product_image}
                  name={product.product_name}
                  price={product.current_price_per_unit}
                  quantity={getProductQuantity(product.product_id)}
                  onQuantityChange={(newQty) => handleQuantityChange(product, newQty)}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="#666">
              {productList ?
                <IllustrationWrapper 
                  illustration={<NoDataFoundSvg />}
                  title="No Data"
                />
              : 'Loading products...'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default CreateDp